import { CommonModule } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { MatBadgeModule } from '@angular/material/badge';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatMenuModule } from '@angular/material/menu';
import { MatSelectModule } from '@angular/material/select';
import { TranslatedRouter, TranslationService } from '@core-services';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { Language, Path } from '@shared-enums';
import { CartEntry } from '@shared-models';
import { setSearchTermAction } from '@store-bicycle-actions';
import { toggleShoppingCartAction } from '@store-shopping-cart-actions';
import { shoppingCartItemsSelector } from '@store-shopping-cart-selectors';
import { debounceTime, distinctUntilChanged, filter } from 'rxjs';

@Component({
  selector: 'app-header',
  standalone: true,
  imports: [
    TranslateModule,
    MatIconModule,
    FormsModule,
    ReactiveFormsModule,
    MatSelectModule,
    MatButtonModule,
    MatInputModule,
    CommonModule,
    MatBadgeModule,
    MatFormFieldModule,
    MatMenuModule,
  ],
  templateUrl: './header.component.html',
  styleUrl: './header.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class HeaderComponent implements OnInit {
  //#region Dependencies

  private router = inject(TranslatedRouter);
  private destroyRef = inject(DestroyRef);
  private store = inject(Store);
  private translationService = inject(TranslationService);

  //#endregion

  //#region Properties

  path = Path;
  language = Language;
  languageForm: FormGroup;
  searchControl = new FormControl('');
  searchInputDisplayed = signal<boolean>(false);
  cartItemsCount = signal<string>('');

  //#endregion

  //#region Lifecycle hooks

  ngOnInit(): void {
    this.initLanguage();
    this.initLanguageChangeSubscription();
    this.initSearchSubscription();
    this.initCartItemsCount();
  }

  //#endregion

  //#region Init

  private initCartItemsCount(): void {
    this.store
      .select(shoppingCartItemsSelector)
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.handleCartItemsCount.bind(this));
  }

  private initLanguage(): void {
    this.languageForm = new FormGroup({
      language: new FormControl(this.translationService.getCurrentLanguage(), []),
    });
  }

  private initLanguageChangeSubscription(): void {
    this.languageForm.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe(this.handleLanguageChange);
  }

  private initSearchSubscription(): void {
    this.searchControl.valueChanges
      .pipe(
        filter(text => text!.length >= 3 || text?.length === 0),
        debounceTime(500),
        distinctUntilChanged(),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe(term => {
        this.store.dispatch(setSearchTermAction({ term: term ?? '' }));
      });
  }

  //#endregion

  //#region UI Methods

  onNavigateTo(path: Path): void {
    this.router.navigateByUrl(
      path.length ? `${Path.BikeCenter}/${path}` : `${Path.BikeCenter}`,
    );
  }

  onHome(): void {
    this.router.navigateByUrl(Path.Root);
  }

  onToggleSearchInput(): void {
    this.searchControl.patchValue('');
    this.searchInputDisplayed.set(!this.searchInputDisplayed());
  }

  onOpenShoppingCart(): void {
    this.store.dispatch(toggleShoppingCartAction({ showCart: true }));
  }

  onClearSearch(): void {
    this.store.dispatch(setSearchTermAction({ term: '' }));
  }

  onChangeLanguage(lang: Language): void {
    this.languageForm.patchValue({
      language: lang,
    });
  }

  //#endregion

  //#region Handlers

  private handleLanguageChange = (change: { language: Language }): void => {
    this.translationService.setCurrentLanguage(change.language);
  };

  private handleCartItemsCount(cartItems: CartEntry[]): void {
    const totalProductsCount = cartItems.reduce(
      (total, obj) => total + obj.products.length,
      0,
    );
    this.cartItemsCount.set(totalProductsCount > 0 ? `${totalProductsCount}` : '');
  }

  //#endregion
}
